<script setup lang="ts">
import gsap from 'gsap'
import type { WorkRecord, ReelRecord } from '~/types/datocms'
import { CardType, AppTheme } from '~/types'
import { useAppStore } from '~/stores/AppStore'

interface CardWorkProps {
  data: WorkRecord | ReelRecord
  type?: CardType
}

const props = withDefaults(defineProps<CardWorkProps>(), {
  type: CardType.WORK as CardType
})

const store = useAppStore()
const borderColor = computed(() =>
  store.theme === AppTheme.DARK ? 'border-white' : 'border-black'
)

const isFullScreenVideoVisible = ref(false)

function handleClick() {
  if (props.type === CardType.WORK) {
    return
  }
  isFullScreenVideoVisible.value = true
}

let ctx: gsap.Context | null = null
const ctxRef = ref<HTMLElement | null>(null)
let tl: GSAPTimeline | null = null

const titleRef = ref<HTMLElement | null>(null)
const clientsRef = ref<HTMLElement | null>(null)
const imageRef = ref<HTMLElement | null>(null)

const { height: titleH } = toRefs(useElementSize(titleRef))

onMounted(() => {
  if (ctxRef.value) {
    const mm = gsap.matchMedia()
    mm.add('(min-width: 1024px)', () => {
      ctx = gsap.context((self: gsap.Context) => {
        const elTitle = self.selector ? self.selector(titleRef.value) : []
        const elClients = self.selector ? self.selector(clientsRef.value) : []
        const elImage = self.selector ? self.selector(imageRef.value?.imageRef) : []

        gsap.set(elClients, { y: titleH.value })
        gsap.set(elTitle, { y: titleH.value })

        tl = gsap
          .timeline({
            paused: true,
            defaults: {
              duration: 0.5,
              ease: 'power3.out'
            }
          })
          .addLabel('start')
          .to(elClients, { y: 0 }, 'start')
          .to(elTitle, { y: 0, delay: 0.1 }, 'start')
          .to(elImage, { duration: 0.9, scale: 1.2 }, 'start')
      }, ctxRef.value as HTMLElement)
    })
  }
})

onUnmounted(() => {
  if (ctx) {
    useTimeoutFn(() => {
      ctx?.revert()
    }, 200)
  }
})
</script>

<template>
  <div
    ref="ctxRef"
    class="group block h-full w-full border-b-2 border-l-2 border-r-0 border-t-0 text-left"
    :class="borderColor"
  >
    <Button
      variant="unstyled"
      :to="
        type === CardType.WORK
          ? usePathByType(data?._modelApiKey, (data.slug as string) ?? undefined)
          : undefined
      "
      class="relative block w-full overflow-hidden"
      :class="borderColor"
      @click="handleClick"
      @mouseenter="() => tl?.play()"
      @mouseleave="() => tl?.reverse()"
    >
      <Image
        v-if="data.image"
        ref="imageRef"
        class="aspect-video w-full max-w-none object-cover lg:aspect-4/3"
        loading="lazy"
        :src="data.image.responsiveImage ? data.image.responsiveImage?.src : data.image.url"
        :srcset="data.image.responsiveImage ? data.image.responsiveImage?.webpSrcSet : undefined"
        :width="
          data.image.responsiveImage
            ? data.image.responsiveImage?.width
            : (data.image.width! ?? 528)
        "
        :height="
          data.image.responsiveImage
            ? data.image.responsiveImage?.height
            : (data.image.height! ?? 396)
        "
        sizes="auto"
        :alt="data.image.alt ?? ''"
      />
      <div class="pointer-events-none absolute left-0 top-0 h-full w-full bg-card-overlay"></div>
      <div
        class="absolute bottom-0 left-0 flex w-full flex-col gap-1 px-4 py-3 leading-tight lg:px-6 lg:py-5"
      >
        <div class="flex flex-col text-white lg:text-lg">
          <template v-if="data._modelApiKey === 'work'">
            <span v-if="data.clients?.length" ref="clientsRef" class="mr-1 block font-bold">{{
              data?.clients[0].name
            }}</span
            ><span class="overflow-hidden text-left text-sm">
              <span ref="titleRef" class="inline-block">{{ data?.title }}</span></span
            >
          </template>
          <template v-else-if="data._modelApiKey === 'reel'">
            <span ref="clientsRef" class="inline-block text-left font-bold">{{ data?.title }}</span>
            <span class="inline-block overflow-hidden text-left text-sm">
              <span ref="titleRef" class="inline-block">{{
                data.subtitle ? data.subtitle : ''
              }}</span>
            </span>
          </template>
        </div>
      </div>
    </Button>
  </div>
  <template v-if="type === CardType.REEL">
    <Transition name="videoOverlay">
      <VideoFullScreen
        v-if="isFullScreenVideoVisible"
        :options="{
          controls: true,
          autoplay: true,
          preload: 'metadata',
          sources: [
            {
              src: data.video?.url,
              type: data.video?.mimeType
            }
          ],
          fill: true,
          bigPlayButton: false,
          controlBar: {
            volumePanel: false,
            playToggle: false,
            fullscreenToggle: false,
            pictureInPictureToggle: false,
            currentTimeDisplay: false,
            remainingTimeDisplay: false
          }
        }"
        @on-close="isFullScreenVideoVisible = false"
      />
    </Transition>
  </template>
</template>
